import React from "react"
import { HelpCircle } from "lucide-react"

import { formatCurrency } from "~/utils/helpers"
import LineItem from "~/types/LineItem"
import ModifierGroup from "~/types/ModifierGroup"

import {
  modifierLimitLabel,
  modifierLimitsForQuantity,
  modifierPricingNote,
  ModifierQuantities,
} from "./lineItemModifierHelpers"

interface Props {
  expanded: boolean
  lineItem: LineItem
  lineItemQuantity: number
  onModifierQuantityChange: (
    lineItem: LineItem,
    group: ModifierGroup,
    modifierId: string,
    value: string
  ) => void
  selectedModifiers: ModifierQuantities
}

const LineItemModifierControls: React.FC<Props> = ({
  expanded,
  lineItem,
  lineItemQuantity,
  onModifierQuantityChange,
  selectedModifiers,
}) => {
  if (!lineItem.modifierGroups?.length) return null

  return (
    <div className="modifiers">
      <div
        className={`modifiers-expander ${expanded ? "is-expanded" : ""}`}
        aria-hidden={!expanded}
      >
        <div className="modifiers-expander-inner">
          <div className="modifiers-content">
            {lineItem.modifierGroups.map((group) => {
              const { minAllowed, maxAllowed } = modifierLimitsForQuantity(group, lineItemQuantity)
              const limitLabel = modifierLimitLabel(minAllowed, maxAllowed)
              const pricingNote = modifierPricingNote(group)

              return (
                <section key={group.id} className="modifier-group">
                  <div className="modifier-header">
                    <div className="modifier-title-row">
                      <div className="modifier-title">
                        <span className="modifier-name">
                          {group.name}
                          {group.required ? " *" : ""}
                        </span>
                        <button
                          type="button"
                          className="modifier-help"
                          title={pricingNote}
                          data-bs-toggle="tooltip"
                          data-bs-placement="top"
                          data-bs-title={pricingNote}
                          aria-label={pricingNote}
                          tabIndex={expanded ? 0 : -1}
                        >
                          <HelpCircle size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="modifier-meta">
                      <span
                        className={`badge ${group.required ? "bg-warning" : "bg-light"} text-dark`}
                      >
                        {group.required ? "Required" : "Optional"}
                      </span>
                      {limitLabel && <span className="badge bg-light text-dark">{limitLabel}</span>}
                    </div>
                  </div>
                  <div className="modifier-body">
                    {group.modifiers.map((modifier) => (
                      <div key={modifier.id} className="modifier-row">
                        <span className="modifier-name-cell">
                          <span className="modifier-name-text">{modifier.name}</span>
                        </span>
                        <input
                          type="number"
                          min={0}
                          value={selectedModifiers[modifier.id] || 0}
                          onChange={(event) =>
                            onModifierQuantityChange(
                              lineItem,
                              group,
                              modifier.id,
                              event.target.value
                            )
                          }
                          className="modifier-quantity"
                          tabIndex={expanded ? 0 : -1}
                        />
                        <span className="modifier-price">
                          {formatCurrency({
                            cents: modifier.priceCents || 0,
                            currency_iso: "USD",
                          })}
                        </span>
                      </div>
                    ))}
                    {group.modifiers.length === 0 && (
                      <span className="modifier-empty">No modifiers defined for this group</span>
                    )}
                  </div>
                </section>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

export default LineItemModifierControls
